import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"

import type { MutatorTx, Schema } from "../schema"
import type {
	CreateContactArgs,
	DeleteContactsArgs,
	SetContactsSubscribedArgs,
	UpdateContactArgs,
} from "../types"
import type { DeleteID } from "@rocicorp/zero"

export const contactMutators = {
	create: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreateContactArgs
			ctx: ZeroJwtPayload
		}) => {
			// Check for existing contact with same email
			const [existingContact] = await tx.query.contacts
				.where("email", "=", args.email)
				.where("workspaceId", "=", authData.workspaceId)
				.run()

			if (existingContact && !existingContact.isDeleted) {
				throw new Error("Contact with this email already exists")
			}

			await tx.mutate.contacts.insert({
				id: args.id,
				email: args.email,
				name: args.name,
				companyId: args.companyId,
				createdAt: Date.now(),
				updatedAt: Date.now(),
				isDeleted: false,
				version: 1,
				workspaceId: authData.workspaceId,
				isSubscribed: true,
				isEmailSuppressed: false,
			})

			// Add segment relationships if provided
			if (args.segmentIds) {
				for (const segmentId of args.segmentIds) {
					await tx.mutate.contactToSegments.insert({
						A: args.id,
						B: segmentId,
					})
				}
			}
		},
	),
	update: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: UpdateContactArgs }) => {
			const companyChanged = "companyId" in args
			const [previous] = companyChanged
				? await tx.query.contacts.where("id", "=", args.id).run()
				: []

			await tx.mutate.contacts.update({
				id: args.id,
				name: args.name,
				email: args.email,
				companyId: args.companyId,
				isSubscribed: args.isSubscribed,
			})

			if (companyChanged) {
				const previousCompanyId = previous?.companyId ?? null
				const nextCompanyId = args.companyId ?? null
				if (previousCompanyId !== nextCompanyId) {
					// A thread sitting on another of the contact's companies was filed
					// there deliberately, so changing the primary must not move it.
					const links = await tx.query.contactCompanies
						.where("contactId", "=", args.id)
						.run()
					const memberships = new Set(links.map((l) => l.companyId))
					const threads = await tx.query.feedbacks
						.where("contactId", "=", args.id)
						.run()
					for (const thread of threads) {
						// keep threads that were manually pointed at a different company
						if ((thread.companyId ?? null) !== previousCompanyId) continue
						if (
							previousCompanyId &&
							memberships.has(previousCompanyId) &&
							memberships.size > 1
						) {
							continue
						}
						await tx.mutate.feedbacks.update({
							id: thread.id,
							companyId: nextCompanyId,
						})
					}
				}
			}
		},
	),
	linkCompany: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: {
				id: string
				primaryLinkId: string
				contactId: string
				companyId: string
			}
			ctx: ZeroJwtPayload
		}) => {
			const links = await tx.query.contactCompanies
				.where("contactId", "=", args.contactId)
				.run()
			if (links.some((l) => l.companyId === args.companyId)) return

			const [contact] = await tx.query.contacts
				.where("id", "=", args.contactId)
				.run()

			// Contacts assigned before memberships existed have a primary but no row
			// for it. Heal that here, so the guards that count rows can trust the
			// count and no bulk backfill is required.
			const primaryCompanyId = contact?.companyId
			if (
				primaryCompanyId &&
				!links.some((l) => l.companyId === primaryCompanyId)
			) {
				await tx.mutate.contactCompanies.insert({
					id: args.primaryLinkId,
					contactId: args.contactId,
					companyId: primaryCompanyId,
					workspaceId: authData.workspaceId,
					createdAt: Date.now(),
				})
			}

			await tx.mutate.contactCompanies.insert({
				id: args.id,
				contactId: args.contactId,
				companyId: args.companyId,
				workspaceId: authData.workspaceId,
				createdAt: Date.now(),
			})

			// A contact with no company yet gets this one as its primary, so the
			// avatar and every single-company read have something to show.
			if (contact && !contact.companyId) {
				await tx.mutate.contacts.update({
					id: args.contactId,
					companyId: args.companyId,
				})
			}
		},
	),
	unlinkCompany: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: { contactId: string; companyId: string }
		}) => {
			const [existing] = await tx.query.contactCompanies
				.where("contactId", "=", args.contactId)
				.where("companyId", "=", args.companyId)
				.run()
			if (!existing) return

			await tx.mutate.contactCompanies.delete({ id: existing.id })

			// The primary has to keep pointing at a company the contact still has.
			const [contact] = await tx.query.contacts
				.where("id", "=", args.contactId)
				.run()
			if (contact?.companyId !== args.companyId) return

			const remaining = await tx.query.contactCompanies
				.where("contactId", "=", args.contactId)
				.run()
			await tx.mutate.contacts.update({
				id: args.contactId,
				companyId: remaining.find((l) => l.companyId !== args.companyId)
					?.companyId,
			})
		},
	),
	delete: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: DeleteID<Schema["tables"]["contacts"]>
		}) => {
			await tx.mutate.contacts.delete({
				id: args.id,
			})
		},
	),
	deleteMany: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: DeleteContactsArgs }) => {
			for (const id of args.ids) {
				await tx.mutate.contacts.delete({ id })
			}
		},
	),
	setSubscribedMany: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: SetContactsSubscribedArgs
		}) => {
			for (const id of args.ids) {
				await tx.mutate.contacts.update({
					id,
					isSubscribed: args.isSubscribed,
				})
			}
		},
	),
}
